import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const SECTIONS = [
  {
    title: '1. Who We Are',
    body: `LocalContact ("we", "us", "our") operates the LocalContact platform, a live search tool for identifying decision makers at local businesses. This Privacy Policy explains how we collect, use, and protect information when you use our Service.`,
  },
  {
    title: '2. Information We Collect',
    body: `We collect the following information when you use the Service: (a) Account data, your name, email address, and payment information when you register; (b) Search inputs, business names, addresses, and job title queries you submit; (c) Search results, contact names and job titles retrieved during your searches; (d) Usage data, logs of searches performed, credits consumed, and pages visited.`,
  },
  {
    title: '3. How We Use Your Information',
    body: `We use the information we collect to: (a) provide and operate the Service; (b) process payments and manage your credit balance; (c) send transactional emails such as purchase confirmations and search completion notifications; (d) improve and debug the platform; (e) comply with legal obligations.`,
  },
  {
    title: '4. Contact Data and 30-Day Deletion',
    body: `All contact data retrieved through live searches (names and job titles of third parties) is automatically and permanently deleted from our systems 30 days after the search is completed. We do not build or maintain a permanent database of contact information. Data you export to CSV becomes your responsibility and must be handled in compliance with applicable data protection laws.`,
  },
  {
    title: '5. Legal Basis for Processing (GDPR)',
    body: `For users in the European Economic Area, we process your personal data under the following legal bases: (a) performance of a contract, to deliver the Service you purchased; (b) legitimate interests, to improve the platform and prevent fraud; (c) compliance with legal obligations, where required by law. You may request access, correction, or deletion of your personal data at any time by contacting us.`,
  },
  {
    title: '6. Data Sharing',
    body: `We do not sell, rent, or trade your personal data to third parties. We may share data with: (a) payment processors (e.g., Razorpay) for billing; (b) cloud infrastructure providers for hosting; (c) email delivery services for transactional communications. All third-party processors are contractually required to protect your data.`,
  },
  {
    title: '7. Data Security',
    body: `We use industry-standard security measures including AES-256 encryption at rest, TLS in transit, and access controls to protect your data. No system is completely secure, and we cannot guarantee the absolute security of your information.`,
  },
  {
    title: '8. Cookies',
    body: `We use essential cookies to maintain your login session. We do not use advertising or tracking cookies. You can disable cookies in your browser settings, but doing so may affect your ability to log in to the Service.`,
  },
  {
    title: '9. Data Retention',
    body: `We retain your account data for as long as your account is active or as required to fulfill our legal obligations. Search result data is deleted after 30 days. You may request deletion of your account and associated data at any time.`,
  },
  {
    title: '10. Your Rights',
    body: `Depending on your location, you may have the right to: access the personal data we hold about you; correct inaccurate data; request deletion of your data; object to or restrict processing; data portability. To exercise any of these rights, contact us at support@localcontact.ai.`,
  },
  {
    title: '11. Changes to This Policy',
    body: `We may update this Privacy Policy from time to time. We will notify you of material changes via email or a notice within the platform. Continued use of the Service after the effective date constitutes acceptance of the updated policy.`,
  },
  {
    title: '12. Contact Us',
    body: `For privacy-related questions or requests, contact us at: support@localcontact.ai`,
  },
];

export default function PrivacyPolicy() {
  useDocumentTitle('Privacy Policy');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* minimal nav */}
      <header className="border-b border-slate-100">
        <div className="mx-auto max-w-4xl px-6 py-5 flex items-center justify-between">
          <Link to="/">
            <img src={logo} alt="LocalContact" className="h-7 w-auto object-contain" />
          </Link>
          <Link to="/register" className="text-[13px] font-bold text-slate-600 hover:text-slate-900 transition-colors">
            Back to sign up
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 py-16 pb-24">
        {/* header */}
        <div className="mb-12">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-brand-700 mb-3">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-[-0.035em] text-slate-900 mb-4">Privacy Policy</h1>
          <p className="text-[14px] text-slate-400 font-medium">Last updated: June 2025</p>
        </div>

        <div className="space-y-10">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <h2 className="text-[17px] font-bold text-slate-900 mb-3">{section.title}</h2>
              <p className="text-[15px] text-slate-600 leading-relaxed">{section.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-slate-100 flex flex-wrap gap-4 text-[13px] text-slate-400">
          <Link to="/terms" className="hover:text-slate-700 transition-colors font-medium">Terms of Service</Link>
          <span>·</span>
          <Link to="/" className="hover:text-slate-700 transition-colors font-medium">Back to home</Link>
        </div>
      </div>
    </div>
  );
}
